import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgModule, OnDestroy, OnInit } from '@angular/core';
import { CurrencyService } from '@csgofast/core/currency-service';
import { AppCurrencyModule, AppPercentModule } from '@csgofast/shared/pipes';
import { ICurrencyRate } from '@dev-fast/types';
import anime from 'animejs';
import { Subscription } from 'rxjs';

type IValueType = 'number' | 'percent' | 'currency';
type IDifferencePosition = 'top' | 'bottom' | 'right' | 'left' | 'hide';

@Component({
  selector: 'fast-ui-increase',
  templateUrl: './increase.component.html',
  styleUrls: ['./increase.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IncreaseComponent implements OnDestroy, OnInit {
  @Input() public index: string | number = Number.MAX_SAFE_INTEGER;
  @Input() public className = 'increase';
  @Input() public valueType: IValueType = 'number';
  @Input() public maxValue: number | null = null;
  @Input() public delay = 0;
  @Input() public set setCurValue(newValue: number) {
    this.animate(newValue);
  }
  @Input() public differencePosition: IDifferencePosition = 'hide';
  @Input() public duration = 600;

  private currency$: Subscription | null = null;
  private animation: anime.AnimeInstance | null = null;
  private target = '';
  public curValue = 0;
  public preValue = 0;
  public difference = 0;
  public isDifferenceShow = false;
  public mainPerfix = '';
  public differencePrefix = '';
  public rank = 1;
  public currency: ICurrencyRate | null = null;

  constructor(private cdr: ChangeDetectorRef, private currencyService: CurrencyService) {}

  ngOnInit(): void {
    if (this.valueType === 'currency') {
      this.currency$ = this.currencyService.currency$.subscribe((currency) => {
        this.currency = currency;
        this.curValue = this.transform(this.curValue);
        this.preValue = this.curValue;
      });
    }
  }

  ngOnDestroy(): void {
    this.currency$?.unsubscribe();
    this.animation = null;
  }
  private animate(value: number): void {
    this.rank = this.valueType === 'percent' ? 1000 : this.valueType === 'currency' ? 100 : 1;
    this.target = `.${this.className}_${this.index}`;
    if (this.valueType === 'currency' && this.currency) {
      this.animateCurrency(value, this.duration);
    } else {
      this.animatePercent(value, this.duration);
    }
  }

  private animatePercent(newValue: number, duration: number): void {
    this.curValue = newValue;
    if (this.maxValue !== null) {
      this.curValue = this.maxValue < this.curValue ? this.maxValue : this.curValue;
      this.preValue = this.maxValue < this.preValue ? this.maxValue : this.preValue;
      if (this.maxValue <= this.curValue) {
        this.mainPerfix = '~';
      } else {
        this.mainPerfix = '';
      }
    }
    if (this.preValue !== 0) {
      this.difference = this.curValue - this.preValue;
      if (this.differencePosition !== 'hide' && this.difference !== 0) {
        this.isDifferenceShow = true;
      }
      if (this.difference > 0) {
        this.differencePrefix = '+';
      } else {
        this.differencePrefix = '-';
      }
      this.animation = anime({
        targets: this.target,
        value: [this.preValue / this.rank, this.curValue / this.rank],
        round: this.rank,
        easing: 'easeInOutExpo',
        duration: duration,
        delay: this.delay,
        complete: () => {
          this.isDifferenceShow = false;
        },
      });
    }
    this.cdr.detectChanges();
    this.preValue = newValue;
  }
  private animateCurrency(newValue: number, duration: number): void {
    const bufferValue = this.transform(newValue);
    if (this.currency) {
      this.difference = Number((bufferValue - this.preValue).toFixed(2));
      if (this.differencePosition !== 'hide' && this.difference !== 0) {
        this.isDifferenceShow = true;
      }
      if (this.difference > 0) {
        this.differencePrefix = '+';
      } else {
        this.differencePrefix = '';
      }
      this.animation = anime({
        targets: this.target,
        value: [this.preValue, bufferValue],
        round: this.rank,
        easing: 'easeInExpo',
        duration: duration,
        delay: this.delay,
        complete: () => {
          this.curValue = this.transform(newValue);
          this.isDifferenceShow = false;
        },
      });
    }
    this.cdr.detectChanges();
    this.preValue = this.transform(newValue);
  }
  private transform(value: number): number {
    return this.currencyService.convert(value);
  }
}
@NgModule({
  declarations: [IncreaseComponent],
  imports: [CommonModule, AppCurrencyModule, AppPercentModule],
  exports: [IncreaseComponent],
})
export class IncreaseModule {}
